/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useCallback, useEffect, useState } from 'react';
import { VisitsService } from '@/services/requests/housien-services/VisitsService';
import type { Visit } from '@/services/requests/housien-models/Visit';

interface UsePropertyVisitsState {
  visits: Visit[];
  loading: boolean;
  error: string | null;
  totalPages: number;
}

/**
 * Loads a paginated list of visits scheduled for a property.
 * @param agencyId - The agency that owns the property.
 * @param propertyId - The property whose visits are listed.
 * @param page - The 1-based page number to fetch.
 * @param limit - The maximum number of visits per page.
 * @returns The visits, loading flag, error, total page count, and a refetch callback.
 */
export function usePropertyVisits(agencyId: string, propertyId: string, page = 1, limit = 20) {
  const [state, setState] = useState<UsePropertyVisitsState>({
    visits: [],
    loading: true,
    error: null,
    totalPages: 0,
  });

  const fetchVisits = useCallback(async () => {
    if (!agencyId || !propertyId) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await VisitsService.getPropertyVisits(
        agencyId,
        page,
        limit,
        undefined,
        undefined,
        undefined,
        propertyId,
      );
      setState({
        visits: response.data ?? [],
        loading: false,
        error: null,
        totalPages: response.pages ?? 0,
      });
    } catch (err) {
      setState({
        visits: [],
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load visits',
        totalPages: 0,
      });
    }
  }, [agencyId, propertyId, page, limit]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  return { ...state, refetch: fetchVisits };
}
